import { createFileRoute, Link } from "@tanstack/react-router";
import { ArticleLayout } from "@/components/ArticleLayout";
import { ogImageFor, ogMetaTags, twitterTextTags } from "@/lib/ogImages";
import { getArticleBySlug } from "@/lib/learningHub";

const SITE_URL = "https://coloradowebimpressions.com";
const PATH = "/learn/ai-vs-human-marketing";
const ARTICLE = getArticleBySlug(PATH);
const OG_IMAGE = `${SITE_URL}${ogImageFor({ articleCategory: ARTICLE?.category })}`;

export const Route = createFileRoute("/learn/ai-vs-human-marketing")({
  head: () => ({
    meta: [
      { title: "Will AI Replace Your Marketing Agency? Honest Answer" },
      { name: "description", content: "After 25 years in Colorado marketing, here's the honest answer on whether AI will replace your agency — and what owners should actually be doing now." },
      { property: "og:title", content: "Will AI Replace Your Marketing Agency? Honest Answer" },
      { property: "og:description", content: "After 25 years in Colorado marketing, here's the honest answer on whether AI will replace your agency — and what owners should actually be doing now." },
      { property: "og:url", content: `${SITE_URL}${PATH}` },
      { property: "og:type", content: "article" },
      ...ogMetaTags(OG_IMAGE),
      ...twitterTextTags({
        title: ARTICLE?.title ?? "AI vs human marketing — the honest answer",
        description: ARTICLE?.desc ?? "Where AI is great, where it's bad, and what it means for your Colorado business.",
      }),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${PATH}` }],
  }),
  component: Article,
});

function Article() {
  return (
    <ArticleLayout
      eyebrow="Marketing Strategy"
      read="9 min read"
      title="Will AI replace your marketing agency? Honest answer from 25 years in"
      intro="Half the agencies are panicked. Half are pretending nothing changed. Both are wrong. Here's what AI is actually good at, where it's genuinely bad, and what that means for a Colorado small business in 2026."
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Learning Hub", to: "/learn" },
        { label: "AI vs human marketing" },
      ]}
      path={PATH}
      faqs={[
        { q: "Will AI replace marketing agencies?", a: "AI will replace agencies that only did commodity work — generic blog posts, basic landing pages, mass keyword targeting. It will not replace consultants and agencies that bring local relationships, judgment, and strategic context. The middle is collapsing; the high end is getting more valuable." },
        { q: "Should I just use ChatGPT to write all my content?", a: "You can use it for first drafts — but published, un-edited AI content gets penalized by both Google and the LLMs themselves. The hybrid model is: AI for the heavy lifting, a human for accuracy, voice, local relevance, and editorial judgment." },
        { q: "What can AI not do for a Colorado small business?", a: "Pick up the phone when a client is panicking about January slow-season. Sit at a job site and understand a contractor's actual workflow. Read the room in a sales meeting. Know that Colorado Springs and Denver are different markets. Tell you the truth when you don't want to hear it." },
      ]}
    >
      <h2>The short version</h2>
      <p>
        AI won't replace you. It will replace people — and agencies — who don't adapt. The same is true for me. I've been doing this for
        25+ years, and 2024–2025 was the first time the work itself genuinely shifted underneath me. Not the strategy. The mechanics.
      </p>
      <p>
        Here's what's actually happening, the way I'd explain it to a friend over coffee.
      </p>

      <h2>What AI is genuinely good at</h2>
      <ul>
        <li><strong>Heavy lifting.</strong> Research summaries, first-draft outlines, brainstorming variations.</li>
        <li><strong>Pattern work.</strong> Schema generation, alt text, meta description first passes.</li>
        <li><strong>Speed.</strong> What used to take a junior copywriter two days now takes 20 minutes — with editing.</li>
        <li><strong>Translation.</strong> Turning industry jargon into customer-friendly language.</li>
      </ul>

      <h2>Where AI is honestly bad</h2>
      <ul>
        <li><strong>Relationships.</strong> AI can't call your client when their leads dropped 30% in January. It can't read panic in a voice. It can't reframe a strategy over coffee.</li>
        <li><strong>Local context.</strong> AI doesn't know that Briargate buyers are different than downtown Denver buyers, or that a Pueblo plumber's phone rings differently in July than January.</li>
        <li><strong>Judgment.</strong> AI will confidently recommend a strategy that's wrong for your specific market, your specific budget, your specific stage of business. It doesn't know what to ignore.</li>
        <li><strong>Truth-telling.</strong> AI will not tell you your idea is bad, your website is hurting you, or that you should not spend $5,000/month on the thing you just got excited about. I will.</li>
        <li><strong>Trust.</strong> When the wheels come off — and they always do at some point — you don't want a chatbot. You want a human who knows your business and has skin in the game.</li>
      </ul>

      <h2>The hybrid model that's actually working</h2>
      <p>
        Every serious marketing operation I trust right now runs the same playbook:
      </p>
      <ol>
        <li><strong>AI does the first 60%.</strong> Research, outlines, drafts, structured data, repetitive optimization tasks.</li>
        <li><strong>A human directs the last 40%.</strong> Editorial judgment, voice, accuracy, local nuance, and the strategic call about what to even publish.</li>
        <li><strong>The human is the editor-in-chief, not the typist.</strong> If you flip that, the output is generic and gets penalized.</li>
      </ol>

      <h2>What this means for your business</h2>
      <p>If you're a Colorado small business owner, here's the practical translation:</p>
      <ul>
        <li><strong>The cheap end of marketing is going to get cheaper and worse.</strong> $99 SEO is now $9 SEO and you'll get exactly what you pay for.</li>
        <li><strong>The strategic, relational end is getting more valuable.</strong> Because the landscape is more confusing, a trusted guide is worth more, not less.</li>
        <li><strong>Hire for judgment, not just deliverables.</strong> Anyone can produce a 1,500-word blog post in 90 seconds. Almost nobody can tell you whether you should.</li>
      </ul>

      <h2>Where I land — and why I built the coaching tier</h2>
      <p>
        I genuinely believe the role most Colorado small businesses need right now is a translator. Someone who can sit across from
        you, hear the whole picture — the slow January, the new competitor, the AI Overview eating your top-ranked page, the
        contractor who keeps over-promising — and reframe what to do next. That role cannot be automated. That's the bet I'm making.
      </p>
      <p>
        That's why I added a{" "}
        <Link to="/ai-visibility-audit" className="font-bold text-brand-gold-deep underline">
          coaching tier alongside the AI Visibility Audit
        </Link>
        . The audit gives you the snapshot. The coaching gives you a human walking next to you for 90 days while the landscape keeps
        shifting. If you'd rather have us just do the work for you, the{" "}
        <Link to="/geo-ai" className="font-bold text-brand-gold-deep underline">
          done-for-you GEO &amp; AI Search service
        </Link>{" "}
        is the other path.
      </p>
      <p>
        The honest bottom line: 10 years from now, the agencies still standing are going to be the ones that figured out the hybrid
        model early. The ones still selling commodity work are going to be gone. You're asking the right questions now, and that puts
        you ahead of most.
      </p>
    </ArticleLayout>
  );
}
